import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { IotApiClient } from "@iot-platform/core";
import type { ReportDailyActivityDto } from "@report/data/dto/report-daily-activity.dto.ts";
import type { ReportDetailedStatesDto } from "@report/data/dto/report-detailed-states.dto.ts";
import type { ReportPoisRoutesDto } from "@report/data/dto/report-pois-routes.dto.ts";
import type { ReportPoisVisitedDto } from "@report/data/dto/report-pois-visited.dto.ts";
import type { ReportRouteGpsDto } from "@report/data/dto/report-route-gps.dto.ts";
import type { ReportStatesGpsDto } from "@report/data/dto/report-states-gps.dto.ts";
import type { ReportTraveledKilometersDto } from "@report/data/dto/report-traveled-kilometers.dto.ts";
import type {
    ReportDto,
    ReportHistoricalCustomFilterBody,
    ReportHistoricalFilterBody,
    ReportHistoricalFilterQuery,
} from "@report/data/dto/report.dto";
import { ReportDailyActivityMapper } from "@report/data/mappers/report-daily-activity.mapper.ts";
import { ReportDetailedStateMapper } from "@report/data/mappers/report-detailed-state.mapper.ts";
import { ReportPoisRoutesMapper } from "@report/data/mappers/report-pois-routes.mapper.ts";
import { ReportPoisVisitedMapper } from "@report/data/mappers/report-pois-visited.mapper.ts";
import { ReportRouteGpsMapper } from "@report/data/mappers/report-route-gps.mapper.ts";
import { ReportStateGpsMapper } from "@report/data/mappers/report-state-gps.mapper.ts";
import { ReportTraveledKilometersMapper } from "@report/data/mappers/report-traveled-kilometers.mapper.ts";
import { ReportMapper } from "@report/data/mappers/report.mapper";
import type { ReportRouteGpsFilter } from "@report/domain/models/report-route-gps.ts";
import {
    ReportGpsTypeEnum,
    ReportRouteGps,
} from "@report/domain/models/report-route-gps.ts";
import type {
    Report,
    ReportHistoricalCustomFilter,
    ReportHistoricalFilter,
} from "@report/domain/models/report.model";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class ReportDatasource {
    static readonly REPORT_HISTORICAL_PATH = "/reports/historical";
    static readonly REPORT_HISTORICAL_REQUEST_MAIL =
        "/reports/background/historical";
    static readonly REPORT_HISTORICAL_CUSTOM_PATH =
        "/reports/historical_custom";
    static readonly REPORT_HISTORICAL_CUSTOM_REQUEST_MAIL =
        "/reports/background/historical_custom";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(ReportMapper)
        private readonly reportMapper: ReportMapper,
        @inject(ReportRouteGpsMapper)
        private readonly reportRouteGpsMapper: ReportRouteGpsMapper,
        @inject(ReportDailyActivityMapper)
        private readonly reportDailyActivityMapper: ReportDailyActivityMapper,
        @inject(ReportDetailedStateMapper)
        private readonly reportDetailedStateMapper: ReportDetailedStateMapper,
        @inject(ReportPoisRoutesMapper)
        private readonly reportPoisRoutesMapper: ReportPoisRoutesMapper,
        @inject(ReportPoisVisitedMapper)
        private readonly reportPoisVisitedMapper: ReportPoisVisitedMapper,
        @inject(ReportStateGpsMapper)
        private readonly reportStateGpsMapper: ReportStateGpsMapper,
        @inject(ReportTraveledKilometersMapper)
        private readonly reportTraveledKilometersMapper: ReportTraveledKilometersMapper,
    ) {}

    fetchHistoricalReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        const query: ReportHistoricalFilterQuery = {
            from: reportHistoricalFilter.from,
            to: reportHistoricalFilter.to,
            station_ids: reportHistoricalFilter.stationIds,
        };

        const reportHistoricalFilterBody: ReportHistoricalFilterBody = {};
        if (reportHistoricalFilter.road) {
            reportHistoricalFilterBody.road = reportHistoricalFilter.road;

            if (reportHistoricalFilter.fromPk !== undefined) {
                reportHistoricalFilterBody.from_pk =
                    reportHistoricalFilter.fromPk;
            }

            if (reportHistoricalFilter.toPk !== undefined) {
                reportHistoricalFilterBody.to_pk = reportHistoricalFilter.toPk;
            }
        }

        return this.iotApiClient.http
            .get<ReportDto>(ReportDatasource.REPORT_HISTORICAL_PATH, {
                body: reportHistoricalFilterBody,
                query,
            })
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    fetchHistoricalCustomReport(
        reportHistoricalCustomFilter: ReportHistoricalCustomFilter,
    ): ResultAsync<Report[], FallbackError> {
        const query = {
            from: reportHistoricalCustomFilter.from,
            to: reportHistoricalCustomFilter.to,
            station_id: reportHistoricalCustomFilter.stationId,
            variable_ids: reportHistoricalCustomFilter.variableIds,
            page: reportHistoricalCustomFilter.page,
            max_values: reportHistoricalCustomFilter.maxValues,
            min_values: reportHistoricalCustomFilter.minValues,
            avg_values: reportHistoricalCustomFilter.avgValues,
            sum_values: reportHistoricalCustomFilter.sumValues,
            measures: reportHistoricalCustomFilter.measures,
            total_sum: reportHistoricalCustomFilter.totalSum,
            tendency: reportHistoricalCustomFilter.tendency,
        };

        const reportHistoricalCustomFilterBody: ReportHistoricalCustomFilterBody =
            {};

        if (reportHistoricalCustomFilter.road) {
            reportHistoricalCustomFilterBody.road =
                reportHistoricalCustomFilter.road;
            if (reportHistoricalCustomFilter.fromPk !== undefined) {
                reportHistoricalCustomFilterBody.from_pk =
                    reportHistoricalCustomFilter.fromPk;
            }

            if (reportHistoricalCustomFilter.toPk !== undefined) {
                reportHistoricalCustomFilterBody.to_pk =
                    reportHistoricalCustomFilter.toPk;
            }
        }

        return this.iotApiClient.http
            .get<ReportDto>(ReportDatasource.REPORT_HISTORICAL_CUSTOM_PATH, {
                body: reportHistoricalCustomFilterBody,
                query,
            })
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    requestMailHistoricalReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<boolean, FallbackError> {
        const queryParams = new URLSearchParams({
            from: reportHistoricalFilter.from.toFixed(0),
            to: reportHistoricalFilter.to.toFixed(0),
            road: "null",
            from_pk: "null",
            to_pk: "1000",
        });

        const optionalParams = [
            ["max_values", reportHistoricalFilter.maxValues],
            ["min_values", reportHistoricalFilter.minValues],
            ["avg_values", reportHistoricalFilter.avgValues],
            ["sum_values", reportHistoricalFilter.sumValues],
            ["measures", reportHistoricalFilter.measures],
            ["total_sum", reportHistoricalFilter.totalSum],
            ["tendency", reportHistoricalFilter.tendency],
        ] as const;

        optionalParams
            .filter(([, value]) => value !== undefined)
            .forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, value.toString());
                }
            });

        // TODO: Create two separate methods for each endpoint
        // TODO: Move the logic of calling one or the other endpoint to the repository
        // TODO: Move the logic of building the URL params with axios params instead of manually building the URL
        let url = "";
        if (reportHistoricalFilter.variableIds?.length) {
            reportHistoricalFilter.variableIds.forEach((variableId) => {
                queryParams.append(
                    "variable_ids[]",
                    encodeURIComponent(variableId.toString()),
                );
            });
            const stationId = reportHistoricalFilter.stationIds[0];
            url = `${ReportDatasource.REPORT_HISTORICAL_CUSTOM_REQUEST_MAIL}?station_id=${stationId}&${queryParams.toString()}`;
        } else {
            const stationIds = reportHistoricalFilter.stationIds
                .map(
                    (id) =>
                        `station_ids%5B%5D=${encodeURIComponent(id.toString())}`,
                )
                .join("&");
            url = `${ReportDatasource.REPORT_HISTORICAL_REQUEST_MAIL}?${stationIds}&${queryParams.toString()}`;
        }

        return this.iotApiClient.http
            .get(url)
            .mapErr(() => new FallbackError())
            .map(() => true);
    }

    fetchRouteGps(
        reportRouteGpsFilter: ReportRouteGpsFilter,
    ): ResultAsync<ReportRouteGps[], FallbackError> {
        const queryParams = new URLSearchParams({
            from: reportRouteGpsFilter.from.toFixed(0),
            to: reportRouteGpsFilter.to.toFixed(0),
        });

        const url = `/stations/${reportRouteGpsFilter.stationId}/route_gps?${queryParams.toString()}`;

        return this.iotApiClient.http
            .get<ReportRouteGpsDto>(url)
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportRouteGpsMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    fetchDailyActivityReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        const stationIds = reportHistoricalFilter.stationIds
            .map(
                (id) =>
                    `station_ids%5B%5D=${encodeURIComponent(id.toString())}`,
            )
            .join("&");

        const queryParams = new URLSearchParams({
            from: reportHistoricalFilter.from.toFixed(0),
            to: reportHistoricalFilter.to.toFixed(0),
        });

        if (reportHistoricalFilter.road) {
            queryParams.set("road", reportHistoricalFilter.road);
            if (reportHistoricalFilter.fromPk !== undefined) {
                queryParams.set(
                    "from_pk",
                    reportHistoricalFilter.fromPk.toString(),
                );
            }

            if (reportHistoricalFilter.toPk !== undefined) {
                queryParams.set(
                    "to_pk",
                    reportHistoricalFilter.toPk.toString(),
                );
            }
        }

        const url = `/reports/${ReportGpsTypeEnum.dailyActivityGps}?${stationIds}&${queryParams.toString()}`;

        return this.iotApiClient.http
            .get<ReportDailyActivityDto>(url)
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportDailyActivityMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    fetchDetailedStateReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        const stationIds = reportHistoricalFilter.stationIds
            .map(
                (id) =>
                    `station_ids%5B%5D=${encodeURIComponent(id.toString())}`,
            )
            .join("&");

        const queryParams = new URLSearchParams({
            from: reportHistoricalFilter.from.toFixed(0),
            to: reportHistoricalFilter.to.toFixed(0),
        });

        if (reportHistoricalFilter.road) {
            queryParams.set("road", reportHistoricalFilter.road);
            if (reportHistoricalFilter.fromPk !== undefined) {
                queryParams.set(
                    "from_pk",
                    reportHistoricalFilter.fromPk.toString(),
                );
            }

            if (reportHistoricalFilter.toPk !== undefined) {
                queryParams.set(
                    "to_pk",
                    reportHistoricalFilter.toPk.toString(),
                );
            }
        }

        const url = `/reports/${ReportGpsTypeEnum.detailedStatesGps}?${stationIds}&${queryParams.toString()}`;

        return this.iotApiClient.http
            .get<ReportDetailedStatesDto>(url)
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportDetailedStateMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    fetchPoisRoutesReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        const stationIds = reportHistoricalFilter.stationIds
            .map(
                (id) =>
                    `station_ids%5B%5D=${encodeURIComponent(id.toString())}`,
            )
            .join("&");

        const queryParams = new URLSearchParams({
            from: reportHistoricalFilter.from.toFixed(0),
            to: reportHistoricalFilter.to.toFixed(0),
        });

        if (reportHistoricalFilter.road) {
            queryParams.set("road", reportHistoricalFilter.road);
            if (reportHistoricalFilter.fromPk !== undefined) {
                queryParams.set(
                    "from_pk",
                    reportHistoricalFilter.fromPk.toString(),
                );
            }

            if (reportHistoricalFilter.toPk !== undefined) {
                queryParams.set(
                    "to_pk",
                    reportHistoricalFilter.toPk.toString(),
                );
            }
        }

        if (reportHistoricalFilter.originPoi) {
            queryParams.set("origin_poi", reportHistoricalFilter.originPoi);
        }

        const url = `/reports/${ReportGpsTypeEnum.poisRoutes}?${stationIds}&${queryParams.toString()}`;

        return this.iotApiClient.http
            .get<ReportPoisRoutesDto>(url)
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportPoisRoutesMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    fetchPoisVisitedReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        const stationIds = reportHistoricalFilter.stationIds
            .map(
                (id) =>
                    `station_ids%5B%5D=${encodeURIComponent(id.toString())}`,
            )
            .join("&");

        const queryParams = new URLSearchParams({
            from: reportHistoricalFilter.from.toFixed(0),
            to: reportHistoricalFilter.to.toFixed(0),
        });

        if (reportHistoricalFilter.road) {
            queryParams.set("road", reportHistoricalFilter.road);
            if (reportHistoricalFilter.fromPk !== undefined) {
                queryParams.set(
                    "from_pk",
                    reportHistoricalFilter.fromPk.toString(),
                );
            }

            if (reportHistoricalFilter.toPk !== undefined) {
                queryParams.set(
                    "to_pk",
                    reportHistoricalFilter.toPk.toString(),
                );
            }
        }

        const url = `/reports/${ReportGpsTypeEnum.poisVisited}?${stationIds}&${queryParams.toString()}`;

        return this.iotApiClient.http
            .get<ReportPoisVisitedDto>(url)
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportPoisVisitedMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    fetchStateGpsReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        const stationIds = reportHistoricalFilter.stationIds
            .map(
                (id) =>
                    `station_ids%5B%5D=${encodeURIComponent(id.toString())}`,
            )
            .join("&");

        const queryParams = new URLSearchParams({
            from: reportHistoricalFilter.from.toFixed(0),
            to: reportHistoricalFilter.to.toFixed(0),
        });

        if (reportHistoricalFilter.road) {
            queryParams.set("road", reportHistoricalFilter.road);
            if (reportHistoricalFilter.fromPk !== undefined) {
                queryParams.set(
                    "from_pk",
                    reportHistoricalFilter.fromPk.toString(),
                );
            }

            if (reportHistoricalFilter.toPk !== undefined) {
                queryParams.set(
                    "to_pk",
                    reportHistoricalFilter.toPk.toString(),
                );
            }
        }

        const url = `/reports/${ReportGpsTypeEnum.statesGps}?${stationIds}&${queryParams.toString()}`;

        return this.iotApiClient.http
            .get<ReportStatesGpsDto>(url)
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportStateGpsMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    fetchTraveledKilometersReport(
        reportHistoricalFilter: ReportHistoricalFilter,
    ): ResultAsync<Report[], FallbackError> {
        const stationIds = reportHistoricalFilter.stationIds
            .map(
                (id) =>
                    `station_ids%5B%5D=${encodeURIComponent(id.toString())}`,
            )
            .join("&");

        const queryParams = new URLSearchParams({
            from: reportHistoricalFilter.from.toFixed(0),
            to: reportHistoricalFilter.to.toFixed(0),
        });

        if (reportHistoricalFilter.road) {
            queryParams.set("road", reportHistoricalFilter.road);
            if (reportHistoricalFilter.fromPk !== undefined) {
                queryParams.set(
                    "from_pk",
                    reportHistoricalFilter.fromPk.toString(),
                );
            }

            if (reportHistoricalFilter.toPk !== undefined) {
                queryParams.set(
                    "to_pk",
                    reportHistoricalFilter.toPk.toString(),
                );
            }
        }

        const url = `/reports/${ReportGpsTypeEnum.traveledKilometers}?${stationIds}&${queryParams.toString()}`;

        return this.iotApiClient.http
            .get<ReportTraveledKilometersDto>(url)
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.reportTraveledKilometersMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }
}
